import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { setInitialPasswordSchema, type SetInitialPasswordInput } from '@udbodhanam/shared';
import apiClient from '../services/apiClient';
import { useAuthStore } from '../stores/useAuthStore';
import ThemeToggle from '../components/UI/ThemeToggle';
import LanguageSelector from '../components/UI/LanguageSelector';

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  );
}

const REQ_CHECKS = [
  { key: 'reqMinLength', test: (v: string) => v.length >= 8 },
  { key: 'reqUppercase', test: (v: string) => /[A-Z]/.test(v) },
  { key: 'reqLowercase', test: (v: string) => /[a-z]/.test(v) },
  { key: 'reqNumber', test: (v: string) => /\d/.test(v) },
  { key: 'reqSpecial', test: (v: string) => /[^A-Za-z0-9]/.test(v) },
] as const;

function PasswordChecklist({ password }: { password: string }) {
  const { t } = useTranslation('auth');
  return (
    <div className="mt-3 rounded-lg border border-border bg-surface-secondary p-3 space-y-1.5">
      <p className="text-[11px] font-semibold text-earth-600 uppercase tracking-wide">
        {t('passwordRequirements.heading')}
      </p>
      {REQ_CHECKS.map(({ key, test }) => {
        const met = password.length > 0 && test(password);
        return (
          <div key={key} className="flex items-center gap-2">
            <span className={met ? 'text-success' : 'text-earth-400'} aria-hidden="true">
              {met ? '✓' : '○'}
            </span>
            <span className={`text-[12px] ${met ? 'text-success' : 'text-earth-500'}`}>
              {t(`passwordRequirements.${key}`)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function ForcePasswordChange() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword);
  const logout = useAuthStore((s) => s.logout);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const form = useForm<SetInitialPasswordInput>({
    resolver: zodResolver(setInitialPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const newPassword = useWatch({ control: form.control, name: 'newPassword' });

  if (!mustChangePassword) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(data: SetInitialPasswordInput) {
    setError('');
    try {
      await apiClient.post('/users/me/set-initial-password', data);
      // Best-effort server-side logout — setInitialPassword already invalidated
      // all refresh-token families, so a failure here is non-fatal.
      await apiClient.post('/auth/logout').catch(() => undefined);
      // Stash the success message in sessionStorage (NOT router state). Calling
      // logout() below flips `mustChangePassword` / `isAuthenticated`, which makes
      // this component's guard (and ProtectedRoute) synchronously fire a stateless
      // <Navigate to="/login"> — that drops any location.state. A sessionStorage
      // flash survives both that redirect and a hard reload; Login reads + clears it.
      sessionStorage.setItem('authFlashKey', 'forcePasswordChange.successMessage');
      logout();
      navigate('/login', { replace: true });
    } catch {
      setError('forcePasswordChange.errorGeneric');
    }
  }

  const isSubmitting = form.formState.isSubmitting;

  return (
    <main className="flex min-h-screen">
      {/* LEFT — orange hero panel */}
      <div className="hidden md:flex flex-1 bg-primary-500 text-white relative overflow-hidden flex-col justify-center px-16 py-16">
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -140, right: -140, width: 460, height: 460, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -50, right: -50, width: 300, height: 300, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ bottom: -180, left: -120, width: 380, height: 380, background: 'rgba(255,255,255,0.08)' }}
        />
        <div className="relative">
          <h1 className="font-display text-[56px] leading-[64px] font-semibold text-white tracking-[-0.01em]">
            Udbodhanam
          </h1>
          <p className="font-display italic text-white/90 text-2xl mt-3 font-medium">
            &ldquo;Awakening through knowledge&rdquo;
          </p>
        </div>
      </div>

      {/* RIGHT — form panel */}
      <div className="w-full md:flex-1 bg-surface flex flex-col relative">
        <div className="absolute top-5 right-5 md:top-7 md:right-8 flex items-center gap-2 z-10">
          <ThemeToggle />
          <LanguageSelector />
        </div>

        <div className="flex-1 flex flex-col md:justify-center px-6 pb-10 pt-16 md:px-[72px] md:py-[60px]">
          <div className="max-w-[440px] w-full mx-auto">
            <h2 className="text-[36px] leading-[44px] font-bold text-earth-800 tracking-[-0.015em]">
              {t('forcePasswordChange.title')}
            </h2>
            <p className="text-earth-600 mt-1.5 text-sm">
              {t('forcePasswordChange.subtitle')}
            </p>

            {/* Context banner */}
            <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
              <p className="text-[13px] font-semibold text-primary-700">
                {t('forcePasswordChange.contextTitle')}
              </p>
              <p className="mt-1 text-[12px] text-primary-600 leading-relaxed">
                {t('forcePasswordChange.contextBody')}
              </p>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)} className="mt-6 space-y-5">
              <div>
                <label className="block text-[13px] font-semibold text-earth-700 mb-1.5">
                  {t('forcePasswordChange.newPassword')}
                </label>
                <div className="relative">
                  <input
                    {...form.register('newPassword')}
                    type={showNew ? 'text' : 'password'}
                    className="w-full h-[42px] border border-border rounded-lg px-[14px] pr-10 text-[14px] text-text bg-surface focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-earth-400 hover:text-earth-600"
                    tabIndex={-1}
                    aria-label={showNew ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon open={showNew} />
                  </button>
                </div>
                {form.formState.errors.newPassword && (
                  <p className="mt-1 text-sm text-error">{form.formState.errors.newPassword.message}</p>
                )}
                <PasswordChecklist password={newPassword ?? ''} />
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-earth-700 mb-1.5">
                  {t('forcePasswordChange.confirmPassword')}
                </label>
                <div className="relative">
                  <input
                    {...form.register('confirmPassword')}
                    type={showConfirm ? 'text' : 'password'}
                    className="w-full h-[42px] border border-border rounded-lg px-[14px] pr-10 text-[14px] text-text bg-surface focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-earth-400 hover:text-earth-600"
                    tabIndex={-1}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
                {form.formState.errors.confirmPassword && (
                  <p className="mt-1 text-sm text-error">{form.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              {error && <p className="text-sm text-error">{t(error)}</p>}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-500 text-white font-semibold rounded-lg py-2.5 text-sm hover:bg-primary-600 active:bg-primary-700 disabled:opacity-50"
              >
                {isSubmitting ? t('forcePasswordChange.submitting') : t('forcePasswordChange.submit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
